import { playingFromSeats, type BoardDef, type FactionId, type Roster } from '@archon3d/core';
import type { StartedGame } from '../net/useRoom';
import { GameSession, type Seat } from './session';

/** A session for the game the server just started, seen from this client's seat. */
export function createOnlineSession(
  game: StartedGame,
  board: BoardDef,
  roster: Roster,
): GameSession {
  const seats: Record<string, Seat> = {};
  const names: Record<FactionId, string> = {};
  const standIns = game.standIns ?? [];
  board.factions.forEach((faction, i) => {
    const kind = game.started.seats[i] ?? 'ai';
    if (kind === 'none') return;
    // A seat the AI stands in for is still a person's; the session hands it to the AI itself.
    const person = kind === 'human' || standIns.includes(i);
    seats[faction.id] = i === game.seat ? 'human' : person ? 'remote' : 'ai';
    const name = game.started.names[i];
    if (person && name) names[faction.id] = name;
  });
  const session = new GameSession({
    board,
    roster,
    definition: game.started.setup.definition,
    seats,
    seed: game.started.seed,
    players: game.started.setup.players,
    // A replay's start has only the seats; they say which sides play all the same.
    playing: game.started.playing ?? playingFromSeats(board, game.started.seats),
    fights: game.started.setup.fights,
    humanFriendlyAi: game.started.setup.humanFriendlyAi,
    engagement: game.started.setup.engagement,
    names,
    online: {
      link: game.link,
      names: game.started.names,
      history: game.history,
      battle: game.battle,
      standIns,
      paused: game.paused,
    },
  });
  game.onSession?.(session);
  return session;
}
